import React from 'react'
import { StaticQuery, withPrefix, graphql } from 'gatsby'
import Helmet from 'react-helmet'

// eslint-disable-next-line react/display-name
export default ({ pageTitle, pageDescription, pathname = '/', ogType, lang, ogImage }) => (
   <StaticQuery
      query={graphql`
         query SeoQuery {
            site {
               siteMetadata {
                  name
                  siteUrl
                  title
                  description
                  seo {
                     separator
                     og {
                        fbAppId
                        type
                        image
                     }
                     twitter {
                        creator
                        card
                     }
                  }
               }
            }
            contentfulGlobalSeo {
               id
               image {
                  url
               }
            }
         }
      `}
      render={(data) => {
         const {
            name,
            siteUrl,
            title,
            description,
            seo: { separator, og, twitter },
         } = data.site.siteMetadata
         const globalOgImage = data?.contentfulGlobalSeo?.image?.url
         const customOgImage = ogImage || globalOgImage || siteUrl + withPrefix(og.image)
         const metaDescription =
            pageDescription && pageDescription !== 'Description of MCC' ? pageDescription : description
         const ogTitle = pageTitle || title

         // Existing Organization structured data
         const organizationSchema = {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Multicoin Capital',
            url: siteUrl,
            logo: {
               '@type': 'ImageObject',
               url: `${siteUrl}/static/img/favicon.png`,
               width: 48,
               height: 41,
            },
            description:
               'Multicoin Capital is a thesis-driven investment firm that invests in cryptocurrencies, tokens, and blockchain companies reshaping trillion-dollar markets.',
            sameAs: ['https://twitter.com/multicoincap', 'https://multicoin.capital/rss.xml'],
            contactPoint: {
               '@type': 'ContactPoint',
               contactType: 'customer service',
               email: 'hello@multicoin.capital',
            },
         }

         // Additional FinancialService structured data
         const financialServiceSchema = {
            '@context': 'https://schema.org',
            '@type': 'FinancialService',
            name: 'Multicoin Capital',
            url: siteUrl,
            image: {
               '@type': 'ImageObject',
               url: `${siteUrl}/static/img/favicon.png`,
               width: 48,
               height: 41,
            },
            description:
               'Multicoin Capital is a thesis-driven investment firm that invests in cryptocurrencies, tokens, and blockchain companies reshaping trillion-dollar markets. We manage a hedge fund and several venture funds, investing in both public and private markets.',
            serviceType: ['Investment Management', 'Hedge Fund', 'Venture Capital'],
            areaServed: 'Worldwide',
            audience: {
               '@type': 'Audience',
               audienceType: 'sophisticated families, foundations, endowments, and institutional investors',
            },
            address: {
               '@type': 'PostalAddress',
               streetAddress: '501 West Ave., Suite 3901',
               addressLocality: 'Austin',
               addressRegion: 'Texas',
               postalCode: '78701',
               addressCountry: 'US',
            },
            contactPoint: [
               {
                  '@type': 'ContactPoint',
                  contactType: 'Investment Inquiries',
                  url: `${siteUrl}/contact`,
               },
               {
                  '@type': 'ContactPoint',
                  contactType: 'Legal Inquiries',
                  email: 'legal@multicoin.capital',
               },
            ],
            hasOfferCatalog: {
               '@type': 'OfferCatalog',
               name: 'Investment Services',
               itemListElement: [
                  {
                     '@type': 'Offer',
                     name: 'Hedge Fund Management',
                     description: 'Managing a portfolio of liquid crypto assets',
                  },
                  {
                     '@type': 'Offer',
                     name: 'Venture Capital',
                     description: 'Early-stage private equity and token deals, often investing in Seed or Series A',
                  },
               ],
            },
         }

         return (
            <Helmet htmlAttributes={{ lang }}>
               <title>{pageTitle && pageTitle !== name ? `${pageTitle} ${separator} ${name}` : title}</title>
               <meta name="description" content={pageDescription || description} />

               {/* OpenGraph tags */}
               <meta property="fb:app_id" content={og.fbAppId} />
               <meta property="og:url" content={siteUrl + pathname} />
               <meta property="og:type" content={ogType || og.type} />
               <meta property="og:title" content={ogTitle} />
               <meta property="og:description" content={metaDescription} />

               {/* Image tags */}
               <meta name="image" content={customOgImage} />
               <meta property="og:image" content={customOgImage} />
               <meta name="twitter:image" content={customOgImage} />

               {/* Twitter Card tags */}
               {twitter && twitter.card && <meta name="twitter:card" content={twitter.card} />}
               {twitter && twitter.creator && <meta name="twitter:creator" content={twitter.creator} />}
               <meta name="twitter:title" content={ogTitle} />
               <meta name="twitter:description" content={metaDescription} />

               {/* Organization structured data (existing) */}
               <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>

               {/* FinancialService structured data (new) */}
               <script type="application/ld+json">{JSON.stringify(financialServiceSchema)}</script>

               {/* LLMs.txt link for AI systems */}
               <link rel="alternate" type="text/markdown" href="/llms.txt" />
            </Helmet>
         )
      }}
   />
)
